// INSIGHTS-DRE: aba de análise do resultado — compara 2 períodos (default
// Q1×Q2 2026) em cima do GET /dre chamado 2x. Zero mudança no motor da DRE.
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  Row, Col, Card, Statistic, Select, Segmented, Button, Alert, Spin, Table,
  Typography, Space, Tag, Empty, App as AntApp,
} from 'antd';
import { FilePdfOutlined, ReloadOutlined, RiseOutlined, FallOutlined } from '@ant-design/icons';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { API_URL } from '../../config';
import {
  DreResp, kpisComparados, composicao, receitaPorArea, ponte, topMovimentosDespesa,
  narrativa, receitaLiquida, reinvestimento, Movimento,
} from './insightsCalc';
import { gerarOnePagerHTML, imprimirOnePager } from './onePagerHTML';
import { LOGO_HYDRODATA_DATAURL } from './logoHydrodata';

const { Text } = Typography;

async function getToken(): Promise<string> {
  const s = await fetchAuthSession();
  return s.tokens?.idToken?.toString() ?? '';
}

const brl = (v: number) =>
  'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const brlFull = (v: number) =>
  'R$ ' + Math.abs(v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// períodos disponíveis (trimestres — o comparativo do semestre é Q1×Q2)
const TRIMESTRES = ['2026/Q1', '2026/Q2', '2026/Q3', '2026/Q4', '2025/Q4', '2025/Q3', '2025/Q2', '2025/Q1'];

export default function InsightsDRE() {
  const { message } = AntApp.useApp();
  const [pa, setPa] = useState('2026/Q1');
  const [pb, setPb] = useState('2026/Q2');
  const [taxSource, setTaxSource] = useState<'estimado' | 'realizado'>('estimado');
  const [dadosA, setDadosA] = useState<DreResp | null>(null);
  const [dadosB, setDadosB] = useState<DreResp | null>(null);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    setLoading(true); setErro(null);
    try {
      const token = await getToken();
      const cfg = { headers: { Authorization: token } };
      // mesma rota do Dashboard (recurso registrado no API Gateway): o form
      // /dre?trimestre= NÃO é recurso do gateway (403 sem CORS → "Network Error").
      const url = (p: string) => `${API_URL}/dre/trimestre/${encodeURIComponent(p)}?tax_source=${taxSource}`;
      const [ra, rb] = await Promise.all([axios.get(url(pa), cfg), axios.get(url(pb), cfg)]);
      const unwrap = (r: any) => (r.data?.body ? JSON.parse(r.data.body) : r.data);
      setDadosA(unwrap(ra)); setDadosB(unwrap(rb));
    } catch (e: any) {
      setErro(e?.response?.data?.erro || e?.message || 'Falha ao carregar a DRE');
    } finally {
      setLoading(false);
    }
  }, [pa, pb, taxSource]);

  useEffect(() => { carregar(); }, [carregar]);

  const kpis = useMemo(() => (dadosA && dadosB ? kpisComparados(dadosA, dadosB) : []), [dadosA, dadosB]);
  const comp = useMemo(() => (dadosA && dadosB ? composicao(dadosA, dadosB) : []), [dadosA, dadosB]);
  const areas = useMemo(() => (dadosA && dadosB ? receitaPorArea(dadosA, dadosB) : []), [dadosA, dadosB]);
  const passos = useMemo(() => (dadosB ? ponte(dadosB) : []), [dadosB]);
  const movs = useMemo(() => (dadosA && dadosB ? topMovimentosDespesa(dadosA, dadosB) : { subiram: [], cairam: [] }), [dadosA, dadosB]);
  const frases = useMemo(() => (dadosA && dadosB ? narrativa(dadosA, dadosB, pa, pb) : []), [dadosA, dadosB, pa, pb]);
  const reinv = useMemo(() => (dadosA && dadosB ? reinvestimento(dadosA, dadosB) : null), [dadosA, dadosB]);

  const exportarPDF = () => {
    if (!dadosA || !dadosB) return;
    const html = gerarOnePagerHTML(dadosA, dadosB, pa, pb, LOGO_HYDRODATA_DATAURL);
    imprimirOnePager(html);
    message.success('One-pager aberto — use "Salvar como PDF" na janela de impressão.');
  };

  const compData = comp.map(c => ({ grupo: c.grupo, [pa]: +c.valorA.toFixed(0), [pb]: +c.valorB.toFixed(0), pctA: c.pctA, pctB: c.pctB }));
  const areaData = areas.map(a => ({ area: a.rotulo, [pa]: +a.valorA.toFixed(0), [pb]: +a.valorB.toFixed(0) }));

  const colMov = (subiu: boolean) => [
    { title: 'Categoria', dataIndex: 'nome', key: 'nome', ellipsis: true },
    { title: pa, dataIndex: 'valorA', key: 'a', align: 'right' as const, width: 100, render: (v: number) => <Text type="secondary">{brl(v)}</Text> },
    { title: pb, dataIndex: 'valorB', key: 'b', align: 'right' as const, width: 100, render: (v: number) => brl(v) },
    {
      title: 'Δ', dataIndex: 'delta', key: 'd', align: 'right' as const, width: 130,
      render: (v: number, r: Movimento) => (
        <Text style={{ color: subiu ? '#dc2626' : '#16a34a', fontWeight: 600 }}>
          {subiu ? '+' : '−'}{brl(v)}{r.deltaPct !== null ? ` (${r.deltaPct >= 0 ? '+' : ''}${r.deltaPct.toFixed(0)}%)` : ''}
        </Text>
      ),
    },
  ];

  const maxPasso = Math.max(1, ...passos.map(p => Math.abs(p.valor)));

  return (
    <div style={{ padding: 16 }}>
      {/* Controles */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Select value={pa} onChange={setPa} style={{ width: 130 }} options={TRIMESTRES.map(t => ({ value: t, label: t }))} />
        <Text type="secondary">vs</Text>
        <Select value={pb} onChange={setPb} style={{ width: 130 }} options={TRIMESTRES.map(t => ({ value: t, label: t }))} />
        <Segmented
          value={taxSource}
          onChange={(v) => setTaxSource(v as any)}
          options={[{ label: 'Estimativa fiscal', value: 'estimado' }, { label: 'DARF/guia real', value: 'realizado' }]}
        />
        <Button icon={<ReloadOutlined />} onClick={carregar} loading={loading}>Atualizar</Button>
        <Button type="primary" icon={<FilePdfOutlined />} onClick={exportarPDF} disabled={!dadosA || !dadosB}>
          Exportar PDF executivo
        </Button>
      </Space>

      {erro && <Alert type="error" showIcon message={erro} style={{ marginBottom: 16 }} />}

      <Spin spinning={loading}>
        {dadosA && dadosB ? (
          <>
            {/* Faixa semestre — inclui o destino do lucro (reinvestido × final) */}
            <Row gutter={[16, 12]} style={{ marginBottom: 16 }}>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0f9ff', borderColor: '#bae6fd', height: '100%' }}>
                  <Statistic title="Receita Líquida (semestre)" value={receitaLiquida(dadosA) + receitaLiquida(dadosB)} precision={0} prefix="R$" />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0f9ff', borderColor: '#bae6fd', height: '100%' }}>
                  <Statistic title="EBITDA (semestre)" value={dadosA.resultadoOp + dadosB.resultadoOp} precision={0} prefix="R$" />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0f9ff', borderColor: '#bae6fd', height: '100%' }}>
                  <Statistic title="Lucro Líquido (semestre)" value={dadosA.lucroLiq + dadosB.lucroLiq} precision={0} prefix="R$" />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', height: '100%' }}>
                  <Statistic title="🌱 Reinvestido na empresa" value={reinv?.investido ?? 0} precision={0} prefix="R$" />
                </Card>
              </Col>
              <Col flex="1 1 200px">
                <Card size="small" style={{ background: '#f0fdf4', borderColor: '#bbf7d0', height: '100%' }}>
                  <Statistic title="Lucro Final (após reinvestir)" value={reinv?.lucroFinal ?? 0} precision={0} prefix="R$" />
                </Card>
              </Col>
            </Row>

            {/* KPIs comparados */}
            <Row gutter={16} style={{ marginBottom: 20 }}>
              {kpis.map(k => {
                const up = k.deltaAbs >= 0;
                const cor = up ? '#16a34a' : '#dc2626';
                return (
                  <Col xs={12} lg={6} key={k.chave}>
                    <Card size="small">
                      <Text type="secondary" style={{ fontSize: 12, textTransform: 'uppercase', letterSpacing: .3 }}>{k.titulo}</Text>
                      <div style={{ fontSize: 22, fontWeight: 700, margin: '4px 0' }}>{brl(k.valorB)}</div>
                      <div style={{ color: cor, fontWeight: 600 }}>
                        {up ? <RiseOutlined /> : <FallOutlined />}{' '}
                        {k.deltaPct === null ? '—' : `${up ? '+' : ''}${k.deltaPct.toFixed(1)}%`}
                        <Text type="secondary" style={{ fontWeight: 400 }}> vs {pa}</Text>
                      </div>
                      {k.margemB !== null && (
                        <div style={{ marginTop: 4 }}>
                          <Tag color={k.refMercado && k.margemB >= k.refMercado[1] ? 'green' : 'blue'}>
                            margem {k.margemB.toFixed(0)}%
                          </Tag>
                          {k.refMercado && <Text type="secondary" style={{ fontSize: 11 }}>mercado {k.refMercado[0]}–{k.refMercado[1]}%</Text>}
                        </div>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>

            {/* Narrativa */}
            {frases.length > 0 && (
              <Alert
                type="info" showIcon style={{ marginBottom: 20 }}
                message="Leitura do período"
                description={<ul style={{ margin: 0, paddingLeft: 18 }}>{frases.map((f, i) => <li key={i}>{f}</li>)}</ul>}
              />
            )}

            <Row gutter={24}>
              {/* Composição de custo/despesa */}
              <Col xs={24} lg={12} style={{ marginBottom: 20 }}>
                <Card size="small" title="Composição de custo/despesa (R$)">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={compData} layout="vertical" margin={{ left: 30, right: 20 }}>
                      <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="grupo" width={140} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: any) => brlFull(Number(v))} />
                      <Legend />
                      <Bar dataKey={pa} fill="#94a3b8" radius={[0, 3, 3, 0]} />
                      <Bar dataKey={pb} fill="#0ea5e9" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                  <Table
                    size="small" pagination={false} style={{ marginTop: 8 }}
                    rowKey="grupo"
                    dataSource={comp}
                    columns={[
                      { title: 'Grupo', dataIndex: 'grupo', key: 'g', ellipsis: true },
                      { title: `% rec. ${pa}`, dataIndex: 'pctA', key: 'pa', align: 'right', width: 90, render: (v: number | null) => v === null ? '—' : `${v.toFixed(1)}%` },
                      { title: `% rec. ${pb}`, dataIndex: 'pctB', key: 'pb', align: 'right', width: 90, render: (v: number | null) => v === null ? '—' : `${v.toFixed(1)}%` },
                    ]}
                  />
                </Card>
              </Col>

              {/* Receita por área */}
              <Col xs={24} lg={12} style={{ marginBottom: 20 }}>
                <Card size="small" title="Receita por área (R$)">
                  <ResponsiveContainer width="100%" height={260}>
                    <BarChart data={areaData} layout="vertical" margin={{ left: 20, right: 20 }}>
                      <XAxis type="number" tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                      <YAxis type="category" dataKey="area" width={110} tick={{ fontSize: 11 }} />
                      <Tooltip formatter={(v: any) => brlFull(Number(v))} />
                      <Legend />
                      <Bar dataKey={pa} fill="#94a3b8" radius={[0, 3, 3, 0]} />
                      <Bar dataKey={pb} fill="#2563eb" radius={[0, 3, 3, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </Card>
              </Col>
            </Row>

            {/* Ponte Receita → EBITDA → Lucro (período B) */}
            <Card size="small" title={`Da receita ao lucro — ${pb}`} style={{ marginBottom: 20 }}>
              {passos.map((p, i) => {
                const w = (Math.abs(p.valor) / maxPasso) * 100;
                const cor = p.tipo === 'subtrai' ? '#fca5a5' : (p.tipo === 'total' ? '#0ea5e9' : '#93c5fd');
                const total = p.tipo === 'total' || p.tipo === 'base';
                return (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '4px 0' }}>
                    <div style={{ width: 170, fontWeight: total ? 700 : 400, fontSize: 12 }}>{p.rotulo}</div>
                    <div style={{ flex: 1, background: '#f1f5f9', borderRadius: 4, height: 20, position: 'relative' }}>
                      <div style={{ width: `${w}%`, background: cor, height: '100%', borderRadius: 4 }} />
                    </div>
                    <div style={{ width: 120, textAlign: 'right', fontWeight: total ? 700 : 400, fontSize: 12, color: p.tipo === 'subtrai' ? '#dc2626' : '#0f172a' }}>
                      {p.tipo === 'subtrai' ? '−' : ''}{brl(p.valor)}
                    </div>
                  </div>
                );
              })}
            </Card>

            {/* REINVEST (2026-07-16): destino do lucro — parte do lucro líquido
                volta pra empresa (grupo INVESTIMENTOS da DRE); só o lucro final
                fica de fato disponível. */}
            {reinv && (
              <Card size="small" title={`🌱 Destino do lucro — ${pa} + ${pb}`} style={{ marginBottom: 20 }}>
                <Row gutter={24}>
                  <Col xs={24} lg={14}>
                    <div style={{ marginBottom: 6, fontSize: 13 }}>
                      <Text type="secondary">Lucro líquido do período: </Text>
                      <Text strong>{brl(reinv.lucroLiq)}</Text>
                    </div>
                    {reinv.lucroLiq > 0 && (
                      <div style={{ display: 'flex', height: 30, borderRadius: 6, overflow: 'hidden', background: '#f1f5f9' }}>
                        <div style={{ width: `${Math.min(100, reinv.pctReinvestido ?? 0)}%`, background: '#16a34a' }} />
                        <div style={{ flex: 1, background: '#0ea5e9' }} />
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginTop: 6, fontSize: 12 }}>
                      <span>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#16a34a', marginRight: 5 }} />
                        Reinvestido na empresa: <Text strong>{brl(reinv.investido)}</Text>
                        {reinv.pctReinvestido !== null && <Text type="secondary"> ({reinv.pctReinvestido.toFixed(0)}% do lucro)</Text>}
                      </span>
                      <span>
                        <span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: '#0ea5e9', marginRight: 5 }} />
                        Lucro Final: <Text strong>{brl(reinv.lucroFinal)}</Text>
                      </span>
                    </div>
                    <Alert
                      type="success" showIcon style={{ marginTop: 12 }}
                      message="O lucro não é distribuído integralmente"
                      description="Parte do resultado volta pra própria empresa como investimento — equipamentos, tecnologia e estrutura — pra sustentar a capacidade de crescer. O que fica de fato disponível é o Lucro Final."
                    />
                  </Col>
                  <Col xs={24} lg={10}>
                    <Table
                      size="small" pagination={false} rowKey="nome"
                      title={() => <Text type="secondary" style={{ fontSize: 12 }}>Em que a empresa investiu</Text>}
                      dataSource={reinv.topCategorias}
                      locale={{ emptyText: 'Sem investimentos registrados no período' }}
                      columns={[
                        { title: 'Categoria', dataIndex: 'nome', key: 'n', ellipsis: true },
                        { title: 'Valor', dataIndex: 'valor', key: 'v', align: 'right', width: 110, render: (v: number) => brl(v) },
                      ]}
                    />
                  </Col>
                </Row>
              </Card>
            )}

            {/* Top movimentos */}
            <Row gutter={24}>
              <Col xs={24} lg={12} style={{ marginBottom: 20 }}>
                <Card size="small" title={<Space><FallOutlined style={{ color: '#dc2626' }} />O que mais subiu de gasto</Space>}>
                  <Table size="small" rowKey="nome" pagination={false} dataSource={movs.subiram} columns={colMov(true)} locale={{ emptyText: 'Sem aumentos relevantes' }} />
                </Card>
              </Col>
              <Col xs={24} lg={12} style={{ marginBottom: 20 }}>
                <Card size="small" title={<Space><RiseOutlined style={{ color: '#16a34a' }} />O que mais caiu de gasto</Space>}>
                  <Table size="small" rowKey="nome" pagination={false} dataSource={movs.cairam} columns={colMov(false)} locale={{ emptyText: 'Sem reduções relevantes' }} />
                </Card>
              </Col>
            </Row>

            <Text type="secondary" style={{ fontSize: 11 }}>
              EBITDA = resultado operacional (sem depreciação lançada). Impostos no modo {taxSource === 'estimado' ? 'estimativa fiscal (Lucro Presumido)' : 'DARF/guia real'}.
              Referência de margem EBITDA para consultoria de engenharia: 10–25%.
              Lucro Final = lucro líquido − investimentos do período (equipamentos, software e estrutura).
            </Text>
          </>
        ) : (!loading && <Empty description="Selecione os períodos para comparar." />)}
      </Spin>
    </div>
  );
}
